import { Button } from 'shared-ui';

import { useLocale } from 'hooks/useLocale/useLocale';
import { AppMessages } from 'i18n/messages';
import * as DetailsStyled from '../components/detailsItem/DetailsItem.styles';
import { dodCommandLabel } from '../components/dodCommands';

import * as Styled from './HistoryTab.styles';
import { HistoryRow } from './history.helpers';

export type HistoryDetailsProps = {
  row: HistoryRow;
  onClose: () => void;
};

const EMPTY_VALUE = '—';

/**
 * Body of the history details panel.
 *
 * Laid out with the application's details trio (`DetailsItemWrapper` / `DetailName` / `DetailText`),
 * the same one the card, event and reader panels use, so the monitor reads like the rest of WebKD.
 * Only the frame and the response keep a typography of their own: they are verbatim wire content,
 * and a proportional face would misalign the comma-separated fields an operator counts.
 *
 * A separate component so it can be tested on its own: the shared-ui `DetailsDrawer` is replaced by
 * a stub in the frontend's jest mock, and content hidden behind it would go unverified.
 *
 * The frame, the controller response and the originating pod are printed when the backend records
 * them. Older entries carry none of them, and the panel then falls back to the command and sequence
 * it does have rather than showing an empty code block.
 */
export const HistoryDetails = ({ row, onClose }: HistoryDetailsProps): JSX.Element => {
  const { formatMessage } = useLocale();

  return (
    <div data-testid="dod-history-details">
      <DetailsStyled.DetailsItemWrapper>
        <DetailsStyled.DetailName>
          {formatMessage({ id: AppMessages['dod.history.details.time'] })}
        </DetailsStyled.DetailName>
        <DetailsStyled.DetailText>{new Date(row.timestamp).toLocaleString()}</DetailsStyled.DetailText>
      </DetailsStyled.DetailsItemWrapper>

      <DetailsStyled.DetailsItemWrapper>
        <DetailsStyled.DetailName>
          {formatMessage({ id: AppMessages['dod.history.details.rcpTkd'] })}
        </DetailsStyled.DetailName>
        <DetailsStyled.DetailText>
          {row.rcpLabel} · {row.tkdLabel}
        </DetailsStyled.DetailText>
      </DetailsStyled.DetailsItemWrapper>

      <DetailsStyled.DetailsItemWrapper>
        <DetailsStyled.DetailName>
          {formatMessage({ id: AppMessages['dod.history.details.command'] })}
        </DetailsStyled.DetailName>
        <DetailsStyled.DetailText>{row.commandName || dodCommandLabel(row.commandType)}</DetailsStyled.DetailText>
      </DetailsStyled.DetailsItemWrapper>

      <DetailsStyled.DetailsItemWrapper>
        <DetailsStyled.DetailName>
          {formatMessage({ id: AppMessages['dod.history.details.sequence'] })}
        </DetailsStyled.DetailName>
        <DetailsStyled.DetailText>{row.sequenceNumber}</DetailsStyled.DetailText>
      </DetailsStyled.DetailsItemWrapper>

      <DetailsStyled.DetailsItemWrapper>
        <DetailsStyled.DetailName>
          {formatMessage({ id: AppMessages['dod.history.details.status'] })}
        </DetailsStyled.DetailName>
        <DetailsStyled.DetailTextBold>
          {formatMessage({ id: AppMessages[`dod.history.status.${row.status}`] })}
        </DetailsStyled.DetailTextBold>
      </DetailsStyled.DetailsItemWrapper>

      <DetailsStyled.DetailsItemWrapper>
        <DetailsStyled.DetailName>
          {formatMessage({ id: AppMessages['dod.history.details.frame'] })}
        </DetailsStyled.DetailName>
        <Styled.FrameBlock>{row.frame || `${row.commandType},${row.sequenceNumber}`}</Styled.FrameBlock>
      </DetailsStyled.DetailsItemWrapper>

      <DetailsStyled.DetailsItemWrapper>
        <DetailsStyled.DetailName>
          {formatMessage({ id: AppMessages['dod.history.details.response'] })}
        </DetailsStyled.DetailName>
        <Styled.FrameBlock>{row.response || EMPTY_VALUE}</Styled.FrameBlock>
      </DetailsStyled.DetailsItemWrapper>

      <DetailsStyled.DetailsItemWrapper>
        <DetailsStyled.DetailName>
          {formatMessage({ id: AppMessages['dod.history.details.error'] })}
        </DetailsStyled.DetailName>
        <DetailsStyled.DetailText>{row.errorMessage ? row.errorMessage : EMPTY_VALUE}</DetailsStyled.DetailText>
      </DetailsStyled.DetailsItemWrapper>

      <DetailsStyled.DetailsItemWrapper>
        <DetailsStyled.DetailName>
          {formatMessage({ id: AppMessages['dod.history.details.duration'] })}
        </DetailsStyled.DetailName>
        <DetailsStyled.DetailText>{row.durationMs} ms</DetailsStyled.DetailText>
      </DetailsStyled.DetailsItemWrapper>

      <DetailsStyled.DetailsItemWrapper>
        <DetailsStyled.DetailName>
          {formatMessage({ id: AppMessages['dod.history.details.source'] })}
        </DetailsStyled.DetailName>
        <DetailsStyled.DetailText>
          {formatMessage({
            id: AppMessages[row.source === 'dod' ? 'dod.history.retransmission' : 'dod.history.directSend'],
          })}
        </DetailsStyled.DetailText>
      </DetailsStyled.DetailsItemWrapper>

      {row.podId && (
        <DetailsStyled.DetailsItemWrapper>
          <DetailsStyled.DetailName>
            {formatMessage({ id: AppMessages['dod.history.details.pod'] })}
          </DetailsStyled.DetailName>
          <DetailsStyled.DetailText>{row.podId}</DetailsStyled.DetailText>
        </DetailsStyled.DetailsItemWrapper>
      )}

      <DetailsStyled.ButtonsWrapper>
        <Button size="small" variant="outlined" onClick={onClose}>
          {formatMessage({ id: AppMessages['common.close'] })}
        </Button>
      </DetailsStyled.ButtonsWrapper>
    </div>
  );
};
